/**
 * Kopstorm add-on upsell
 * ---------------------------------------------------------------------------
 * Powers the <kop-addon-upsell> checkbox row rendered by snippets/kop-addon-upsell.liquid.
 *
 * Responsibilities:
 *   1. Keep the add-on variant in sync with the variant chosen on the product page.
 *   2. Keep the displayed add-on price (and the optional struck through price) correct.
 *   3. Keep the add to cart button total equal to the product price plus every ticked add-on.
 *
 * Adding to cart is NOT handled here. The theme's own product form submit already converts the
 * submit into a multi line add whenever a ".custom_upsells_item_input" is checked.
 */
(function () {
  'use strict';

  /**
   * Builds a money formatter from a sample string rendered by Shopify's money filter, so the
   * number we write into the button is formatted exactly like the one the server rendered.
   */
  function createMoneyFormatter(sample) {
    var fallback = function (cents) {
      return (cents / 100).toFixed(2);
    };

    if (typeof sample !== 'string' || !/\d/.test(sample)) {
      return fallback;
    }

    var first = sample.search(/\d/);
    var last = sample.length - 1;
    while (last >= 0 && !/\d/.test(sample.charAt(last))) {
      last -= 1;
    }

    var prefix = sample.slice(0, first);
    var suffix = sample.slice(last + 1);
    var core = sample.slice(first, last + 1);
    var separators = core.replace(/\d/g, '');

    var decimalSeparator = '';
    var groupSeparator = '';
    var decimals = 0;

    if (separators.length > 0) {
      var lastSeparator = separators.charAt(separators.length - 1);
      var digitsAfter = core.length - core.lastIndexOf(lastSeparator) - 1;

      if (digitsAfter === 2) {
        decimalSeparator = lastSeparator;
        decimals = 2;
        groupSeparator = separators.length > 1 ? separators.charAt(0) : '';
      } else {
        groupSeparator = lastSeparator;
      }
    }

    return function (cents) {
      var value = Math.round(Math.abs(cents));
      var units = decimals === 2 ? Math.floor(value / 100) : Math.round(value / 100);
      var body = String(units);

      if (groupSeparator) {
        body = body.replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator);
      }

      if (decimals === 2) {
        body += decimalSeparator + String(value % 100).padStart(2, '0');
      }

      return (cents < 0 ? '-' : '') + prefix + body + suffix;
    };
  }

  function normalize(value) {
    return String(value == null ? '' : value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');
  }

  function applyDiscount(price, discount) {
    if (!discount || discount <= 0) {
      return price;
    }

    return Math.round((price * (100 - discount)) / 100);
  }

  /**
   * Finds the text node inside a button that holds the "label - price" string.
   */
  function findPriceTextNode(button) {
    var walker = document.createTreeWalker(button, NodeFilter.SHOW_TEXT, null);
    var node;

    while ((node = walker.nextNode())) {
      if (node.data.indexOf(' - ') !== -1) {
        return node;
      }
    }

    return null;
  }

  /**
   * Rewrites every add to cart button that belongs to this form and carries a price.
   * The sticky bar button has no price, so it is left alone.
   */
  function updateButtons(formId) {
    var elements = Array.prototype.slice.call(
      document.querySelectorAll('kop-addon-upsell[form="' + formId + '"]')
    );

    if (!elements.length) {
      return;
    }

    var basePrice = parseInt(elements[0].getAttribute('data-base-price'), 10);
    var formatMoney = null;

    elements.forEach(function (element) {
      if (!formatMoney && typeof element.formatMoney === 'function') {
        formatMoney = element.formatMoney;
      }
    });

    if (isNaN(basePrice) || !formatMoney) {
      return;
    }

    var total = basePrice;

    elements.forEach(function (element) {
      if (typeof element.selectedPrice !== 'number' || element.hidden) {
        return;
      }

      var input = element.querySelector('[data-kop-addon-input]');

      if (input && input.checked) {
        total += element.selectedPrice;
      }
    });

    var buttons = Array.prototype.slice.call(
      document.querySelectorAll('buy-buttons[form="' + formId + '"] button[type="submit"]')
    );

    buttons.forEach(function (button) {
      if (!button.closest('.product-info__block-item')) {
        return;
      }

      // Only the text node holding the price is touched, so any icon markup inside the button survives.
      var textNode = findPriceTextNode(button);

      if (!textNode) {
        // The button carries no price, so there is nothing to keep in sync.
        return;
      }

      var label = button.getAttribute('data-kop-atc-label');

      if (label === null) {
        label = textNode.data.slice(0, textNode.data.lastIndexOf(' - '));
        button.setAttribute('data-kop-atc-label', label);
      }

      var next = label + ' - ' + formatMoney(total);

      if (textNode.data !== next) {
        textNode.data = next;
      }
    });
  }

  var KopAddonUpsell = class extends HTMLElement {
    connectedCallback() {
      this.config = this.readConfig();

      if (!this.config) {
        return;
      }

      this.formatMoney = createMoneyFormatter(this.config.moneySample);
      this.input = this.querySelector('[data-kop-addon-input]');
      this.select = this.querySelector('[data-kop-addon-variant]');
      this.priceElement = this.querySelector('[data-kop-addon-price]');
      this.compareElement = this.querySelector('[data-kop-addon-compare]');
      this.imageElement = this.querySelector('.kop-addon__image');
      this.currentOptions = Array.isArray(this.config.baseOptions) ? this.config.baseOptions : [];

      this.onInputChange = this.onInputChange.bind(this);
      this.onSelectChange = this.onSelectChange.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);

      if (this.input) {
        this.input.addEventListener('change', this.onInputChange);
      }

      if (this.select) {
        this.select.addEventListener('change', this.onSelectChange);
      }

      this.form = document.forms[this.getAttribute('form')] || null;

      if (this.form) {
        this.form.addEventListener('variant:change', this.onVariantChange);
      }

      this.resolveVariant();
      updateButtons(this.getAttribute('form'));
    }

    disconnectedCallback() {
      if (this.input) {
        this.input.removeEventListener('change', this.onInputChange);
      }

      if (this.select) {
        this.select.removeEventListener('change', this.onSelectChange);
      }

      if (this.form) {
        this.form.removeEventListener('variant:change', this.onVariantChange);
      }
    }

    readConfig() {
      var element = this.querySelector('[data-kop-addon-config]');

      if (!element) {
        return null;
      }

      try {
        return JSON.parse(element.textContent);
      } catch (error) {
        return null;
      }
    }

    onInputChange() {
      updateButtons(this.getAttribute('form'));
    }

    onSelectChange() {
      this.applyVariant(this.findVariantById(this.select.value));
      updateButtons(this.getAttribute('form'));
    }

    onVariantChange(event) {
      var variant = event && event.detail ? event.detail.variant : null;

      if (variant && typeof variant.price === 'number') {
        this.setAttribute('data-base-price', variant.price);
      }

      if (variant && Array.isArray(variant.options)) {
        this.currentOptions = variant.options;
      }

      this.resolveVariant();
      updateButtons(this.getAttribute('form'));
    }

    findVariantById(id) {
      var match = null;

      (this.config.variants || []).forEach(function (variant) {
        if (String(variant.id) === String(id)) {
          match = variant;
        }
      });

      return match;
    }

    firstAvailableVariant() {
      var match = null;

      (this.config.variants || []).forEach(function (variant) {
        if (!match && variant.available) {
          match = variant;
        }
      });

      return match;
    }

    /**
     * Picks the add-on variant that belongs with the variant currently selected on the page.
     * In "auto" mode a match means the add-on variant shares an option value (the phone model)
     * with the product variant.
     */
    resolveVariant() {
      var mode = this.config.mode || 'auto';

      if (mode === 'manual') {
        this.applyVariant(this.findVariantById(this.select ? this.select.value : null) || this.firstAvailableVariant());
        return;
      }

      if (mode === 'first') {
        this.applyVariant(this.firstAvailableVariant());
        return;
      }

      var wanted = (this.currentOptions || []).map(normalize).filter(Boolean);
      var match = null;

      (this.config.variants || []).forEach(function (variant) {
        if (match || !variant.available) {
          return;
        }

        var options = (variant.options || []).map(normalize);
        var shared = options.some(function (option) {
          return option && wanted.indexOf(option) !== -1;
        });

        if (shared) {
          match = variant;
        }
      });

      if (!match && !this.config.hideWhenNoMatch) {
        match = this.firstAvailableVariant();
      }

      this.applyVariant(match);
    }

    applyVariant(variant) {
      if (!variant || !variant.available) {
        this.selectedPrice = null;
        this.hidden = true;

        if (this.input) {
          // A hidden row must never end up in the cart.
          this.input.checked = false;
          this.input.setAttribute('data-product-price', '0');
        }

        return;
      }

      this.hidden = false;

      var price = applyDiscount(variant.price, this.config.discount);
      var compare = 0;

      if (this.config.discount > 0) {
        compare = variant.price;
      } else if (this.config.showCompareAt && variant.compare > variant.price) {
        compare = variant.compare;
      }

      this.selectedPrice = price;

      if (this.select && String(this.select.value) !== String(variant.id)) {
        this.select.value = String(variant.id);
      }

      if (this.input) {
        this.input.setAttribute('data-product-price', String(price));
      }

      if (this.priceElement) {
        this.priceElement.textContent = this.formatMoney(price);
      }

      if (this.compareElement) {
        if (compare > 0) {
          this.compareElement.textContent = this.formatMoney(compare);
          this.compareElement.hidden = false;
        } else {
          this.compareElement.textContent = '';
          this.compareElement.hidden = true;
        }
      }

      // The thumbnail follows the matched variant, unless the block pins its own image.
      if (this.imageElement && !this.config.lockImage && variant.image) {
        if (this.imageElement.getAttribute('src') !== variant.image) {
          this.imageElement.setAttribute('src', variant.image);
          this.imageElement.removeAttribute('srcset');
        }
      }
    }
  };

  if (!window.customElements.get('kop-addon-upsell')) {
    window.customElements.define('kop-addon-upsell', KopAddonUpsell);
  }

  /**
   * The theme replaces the whole <buy-buttons> element on every variant change, which restores the
   * server rendered price. The observer re-applies the add-on total right after that swap.
   */
  var observer = new MutationObserver(function (mutations) {
    var forms = [];

    mutations.forEach(function (mutation) {
      Array.prototype.slice.call(mutation.addedNodes).forEach(function (node) {
        if (node.nodeType !== 1) {
          return;
        }

        var element = node.matches && node.matches('buy-buttons')
          ? node
          : node.querySelector && node.querySelector('buy-buttons');

        if (!element) {
          return;
        }

        var formId = element.getAttribute('form');

        if (formId && forms.indexOf(formId) === -1) {
          forms.push(formId);
        }
      });
    });

    forms.forEach(updateButtons);
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
